import { useCallback, useEffect, useState } from 'react';
import {
  Loader,
  AlertCircle,
  Package,
  Plus,
  Pencil,
  Trash2,
  X,
  Check,
  ChevronDown,
  ChevronUp,
  PartyPopper,
  Briefcase,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { eventTeamApiService } from '../../services/eventTeamApi';
import type { EventModpackLink } from '../../types/eventTeam';
import type { EventTeamPerms } from '../../utils/eventTeamPermissions';
import { cn } from '../../utils/cn';
import { SectionHeader } from '../ui/SectionHeader';
import { EmptyState } from '../ui/EmptyState';
import { SkeletonLoader } from '../SkeletonLoader';

export interface EventModpacksSectionProps {
  targetType: 'event' | 'project';
  targetId: string;
  perms: EventTeamPerms;
  refreshNonce?: number;
  onChanged?: () => void;
}

/** Modpack links (guide §1.4) with per-row reverse lookup by modpack name. */
export const EventModpacksSection = ({
  targetType,
  targetId,
  perms,
  refreshNonce = 0,
  onChanged,
}: EventModpacksSectionProps) => {
  const navigate = useNavigate();
  const [links, setLinks] = useState<EventModpackLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState('');
  const [newVersion, setNewVersion] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [adding, setAdding] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editVersion, setEditVersion] = useState('');
  const [editNotes, setEditNotes] = useState('');

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [reverse, setReverse] = useState<EventModpackLink[] | null>(null);
  const [reverseLoading, setReverseLoading] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const data = await eventTeamApiService.listModpackLinks(targetType, targetId);
      setLinks(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load modpack links');
    } finally {
      setLoading(false);
    }
  }, [targetId, targetType]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  useEffect(() => {
    if (refreshNonce === 0) return;
    void load();
  }, [refreshNonce, load]);

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setAdding(true);
    try {
      await eventTeamApiService.createModpackLink(targetType, targetId, {
        modpack_name: newName.trim(),
        modpack_version: newVersion.trim() || null,
        notes: newNotes.trim() || null,
      });
      setNewName('');
      setNewVersion('');
      setNewNotes('');
      setShowAdd(false);
      await load();
      onChanged?.();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to link modpack');
    } finally {
      setAdding(false);
    }
  };

  const handleSaveEdit = async (link: EventModpackLink) => {
    if (!editName.trim()) return;
    setBusyId(link.id);
    try {
      await eventTeamApiService.updateModpackLink(link.id, {
        modpack_name: editName.trim(),
        modpack_version: editVersion.trim() || null,
        notes: editNotes.trim() || null,
      });
      setEditingId(null);
      await load();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update modpack link');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (link: EventModpackLink) => {
    if (!confirm(`Remove modpack link "${link.modpack_name}"?`)) return;
    setBusyId(link.id);
    try {
      await eventTeamApiService.deleteModpackLink(link.id);
      await load();
      onChanged?.();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete modpack link');
    } finally {
      setBusyId(null);
    }
  };

  const toggleReverse = async (link: EventModpackLink) => {
    if (expandedId === link.id) {
      setExpandedId(null);
      setReverse(null);
      return;
    }
    setExpandedId(link.id);
    setReverse(null);
    setReverseLoading(true);
    try {
      const results = await eventTeamApiService.getModpackLinksByName(link.modpack_name);
      setReverse(results);
    } catch {
      setReverse([]);
    } finally {
      setReverseLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-2" aria-busy="true">
        <SkeletonLoader className="h-4 w-32" />
        {Array.from({ length: 3 }).map((_, i) => (
          <SkeletonLoader key={i} className="h-11 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <SectionHeader
        title="Modpacks"
        icon={Package}
        iconClassName="text-orange-400"
        count={links.length}
        className="mb-0 flex-wrap"
        actions={
          perms.canCreate ? (
            <button type="button" onClick={() => setShowAdd(!showAdd)} className="glass-button-sm" aria-expanded={showAdd}>
              <Plus className="w-3.5 h-3.5" />
              Link modpack
            </button>
          ) : undefined
        }
      />

      {error && (
        <div className="alert alert-danger" role="alert">
          <AlertCircle />
          <span className="flex-1">{error}</span>
        </div>
      )}

      {showAdd && (
        <div className="subcard p-3 flex items-center gap-2 flex-wrap">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Modpack name"
            className="glass-input text-sm flex-1 min-w-[180px]"
            autoFocus
          />
          <input
            type="text"
            value={newVersion}
            onChange={(e) => setNewVersion(e.target.value)}
            placeholder="Version (optional)"
            className="glass-input text-sm w-36"
          />
          <input
            type="text"
            value={newNotes}
            onChange={(e) => setNewNotes(e.target.value)}
            placeholder="Notes (optional)"
            className="glass-input text-sm flex-1 min-w-[140px]"
          />
          <button type="button" onClick={() => setShowAdd(false)} className="glass-button-secondary btn-xs">
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleAdd()}
            disabled={adding || !newName.trim()}
            className="glass-button-sm"
          >
            {adding ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            Link
          </button>
        </div>
      )}

      {links.length === 0 && !error ? (
        <EmptyState bare compact icon={Package} title="No modpacks linked" />
      ) : (
        <div className="space-y-2">
          {links.map((link) => {
            const busy = busyId === link.id;
            const expanded = expandedId === link.id;
            const isEditing = editingId === link.id;
            return (
              <div key={link.id} className="subcard">
                {isEditing ? (
                  <div className="p-3 flex items-center gap-2 flex-wrap">
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="glass-input text-sm flex-1 min-w-[160px]"
                    />
                    <input
                      type="text"
                      value={editVersion}
                      onChange={(e) => setEditVersion(e.target.value)}
                      placeholder="Version"
                      className="glass-input text-sm w-32"
                    />
                    <input
                      type="text"
                      value={editNotes}
                      onChange={(e) => setEditNotes(e.target.value)}
                      placeholder="Notes"
                      className="glass-input text-sm flex-1 min-w-[120px]"
                    />
                    <button
                      type="button"
                      onClick={() => setEditingId(null)}
                      className="btn-icon btn-icon-sm"
                      title="Cancel"
                      aria-label="Cancel editing"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleSaveEdit(link)}
                      disabled={busy || !editName.trim()}
                      className="btn-icon btn-icon-sm text-green-400"
                      title="Save"
                      aria-label="Save modpack link"
                    >
                      {busy ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-2 px-3 py-2 flex-wrap">
                    <div className="flex items-center gap-2 min-w-0 flex-wrap">
                      <Package className="w-4 h-4 text-orange-400 flex-shrink-0" />
                      <span className="text-sm text-white font-medium">{link.modpack_name}</span>
                      {link.modpack_version && (
                        <span className="chip font-mono bg-orange-500/10 text-orange-300 border-orange-500/20">
                          {link.modpack_version}
                        </span>
                      )}
                      {link.notes && (
                        <span className="text-[11px] text-glass-500 italic truncate max-w-[240px]" title={link.notes}>
                          {link.notes}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => void toggleReverse(link)}
                        className="btn-icon btn-icon-sm"
                        title="Find other events/projects using this modpack"
                        aria-label="Find other uses of this modpack"
                        aria-expanded={expanded}
                      >
                        {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                      </button>
                      {perms.canCreate && (
                        <>
                          <button
                            onClick={() => {
                              setEditingId(link.id);
                              setEditName(link.modpack_name);
                              setEditVersion(link.modpack_version ?? '');
                              setEditNotes(link.notes ?? '');
                            }}
                            disabled={busy}
                            className="btn-icon btn-icon-sm"
                            title="Edit"
                            aria-label="Edit modpack link"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => void handleDelete(link)}
                            disabled={busy}
                            className="btn-icon btn-icon-sm text-red-400 hover:text-red-300"
                            title="Delete"
                            aria-label="Delete modpack link"
                          >
                            {busy ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {expanded && !isEditing && (
                  <div className="px-3 pb-2 border-t border-glass-200/10 pt-2">
                    <p className="eyebrow mb-1">Other events/projects using "{link.modpack_name}"</p>
                    {reverseLoading ? (
                      <Loader className="w-4 h-4 animate-spin text-glass-400" />
                    ) : !reverse ||
                      reverse.filter((r) => !(r.target_id === targetId && r.target_type === targetType)).length === 0 ? (
                      <p className="text-xs text-glass-500">Nothing else uses this modpack.</p>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {reverse
                          .filter((r) => !(r.target_id === targetId && r.target_type === targetType))
                          .map((r) => (
                            <button
                              key={r.id}
                              onClick={() =>
                                navigate(
                                  r.target_type === 'event'
                                    ? `/event-team/events/${r.target_id}`
                                    : `/event-team/projects/${r.target_id}`,
                                )
                              }
                              className={cn('chip font-mono', 'hover:text-white hover:border-glass-200/40 transition-colors')}
                            >
                              {r.target_type === 'event' ? (
                                <PartyPopper className="w-3 h-3 text-accent-400" />
                              ) : (
                                <Briefcase className="w-3 h-3 text-blue-400" />
                              )}
                              {r.target_id}
                              {r.modpack_version ? ` (${r.modpack_version})` : ''}
                            </button>
                          ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
